import React from 'react';
import { observer } from 'mobx-react-lite';
import NpvrSlider, { NpvrSliderItem } from './npvr-slider';
import { DeleteIcon } from '@smart-tv/components/icons/delete';
import { IconTypes, useToast } from '@smart-tv/hooks/use-toast/use-toast';
import { NpvrEventItem } from '@smart-tv/typings/npvr';
import { rootStore } from './_stores/store-root';
import { NpvrResourceKey } from './_stores/store-npvr';
import { mapNpvrEventToCardProps } from '@smart-tv/utils/kmm/helpers';
import deleteRecording from '@smart-tv/utils/kmm/deleteRecording';
import useDialog from '@smart-tv/hooks/use-dialog';
import { getNPVRActionFocusKey, getNPVRTabFocusKey, NpvrTab, NpvrTabProps } from './npvr-helpers';
import useDelayedSpinner from '@smart-tv/hooks/use-delayed-spinner/use-delayed-spinner';
import NpvrLoading from './npvr-loading';

export const NpvrRecordings: React.FC<NpvrTabProps> = observer(({ setDialogProps }) => {
  const { data: recordings } = rootStore.npvrStore.recordings;
  const { showToast } = useToast();
  const { handleSetDialogFocusKey } = useDialog();
  const showSpinner = useDelayedSpinner(recordings === undefined);

  const handleDeleteRecording = async (event: NpvrEventItem) => {
    try {
      const deleted = await deleteRecording(
        event.sid,
        event.eventId,
        undefined,
        undefined,
        false,
        false,
        false
      );

      if (deleted) {
        const current = rootStore.npvrStore.recordings.data ?? [];
        const indexOfDeletedRecording = current.findIndex(
          (recording) => recording.eventId === event.eventId
        );
        const remainingCount = current.length - 1;

        rootStore.npvrStore.removeRecording(event.eventId);
        rootStore.npvrStore.invalidate([NpvrResourceKey.Status]);

        handleSetDialogFocusKey(
          remainingCount > 0
            ? getNPVRActionFocusKey(
                Math.max(0, Math.min(indexOfDeletedRecording, remainingCount - 1))
              )
            : getNPVRTabFocusKey(NpvrTab.Storage)
        );
        showToast('Optagelse slettet', IconTypes.success);
      } else {
        showToast('Der opstod en fejl under sletning af optagelsen', IconTypes.failure);
      }
    } catch (error) {
      console.error(error);
      showToast('Der opstod en fejl under sletning af optagelsen', IconTypes.failure);
    } finally {
      setDialogProps(null);
    }
  };

  const openDeleteDialog = (event: NpvrEventItem) =>
    setDialogProps({
      titleText: 'Er du sikker på, at du vil slette optagelsen?',
      description:
        (event.serieTitle ?? '') +
        (event.metadataSeasonNo !== null && event.metadataEpisodeNo
          ? ` - Sæson ${event.metadataSeasonNo}, Episode ${event.metadataEpisodeNo} `
          : '') +
        (event.episodeTitle !== null && event.episodeTitle !== event.serieTitle
          ? ` - ${event.episodeTitle}`
          : ''),

      primaryButtonText: 'Slet optagelse',
      primaryButtonFunction: () => handleDeleteRecording(event),
      secondaryButtonText: 'Fortryd',
      secondaryButtonFunction: () => setDialogProps(null)
    });

  React.useEffect(() => {
    rootStore.npvrStore.fetchRecordings().catch((error) => console.error(error));
  }, []);

  if (showSpinner) return <NpvrLoading />;
  if (recordings === undefined) return null;

  const sliderItems: NpvrSliderItem[] = recordings.map((event) => ({
    ...mapNpvrEventToCardProps(event, true),
    action: {
      label: 'Slet',
      icon: <DeleteIcon />,
      onClick: () => openDeleteDialog(event)
    }
  }));

  return (
    <NpvrSlider
      items={sliderItems}
      displayString={recordings.length === 0 ? 'Du har ingen optagelser.' : ''}
    />
  );
});

export default NpvrRecordings;
